import { useState } from "react";
import { gql, useQuery, useMutation } from "@apollo/client";
import Button from "../../../../components/Button";
import { ArrowRight } from "../../../../components/Icons/general-interface-1";
import Modal from "../../../../components/Modal";
import NotificationBanner from "../../../../components/NotificationBanner";
import UserForm, { NewUserDataType } from "../../../../components/User/UserForm";
import NewUserConfirmation from "../../../../components/User/NewUserConfirmation";
import { isAuthorizer, isSalesRep } from "../../../../utils/user";
import { UserType, UserRoleTypeType } from "../../../../types/userType";
import { sanitizeCreateUserData } from "../../../../utils/forms/sanitization";
import styles from "../index.module.scss";

const CREATE_USER = gql`
  mutation CreateUser($input: createUserInput) {
    createUser(input: $input) {
      user {
        id
        first_name
        last_name
        email
        role {
          type
        }
      }
    }
  }
`;

const GET_LOCATIONS = gql`
  query Locations($limit: Int, $sort: String, $start: Int, $where: JSON) {
    locations(limit: $limit, sort: $sort, start: $start, where: $where) {
      id
      name
    }
  }
`;

const GET_ROLES = gql`
  query Roles($limit: Int, $sort: String, $start: Int, $where: JSON) {
    roles(limit: $limit, sort: $sort, start: $start, where: $where) {
      id
      name
      type
    }
  }
`;

type PropsType = {
  dashboard?: UserRoleTypeType;
};

const CreateNewUserButton = ({
  dashboard,
} : PropsType) => {
  const [error, setError] = useState<string>();
  const [isOpen, setIsOpen] = useState(false);
  const [isConfirmationModalOpen, setConfirmationModalOpen] = useState(false);
  const [newUser, setNewUser] = useState<UserType | null>(null);
  const [, setBoundaryError] = useState<Error | null>(null);
  const { data: locationData, client } = useQuery(GET_LOCATIONS, {
    onError: (error) => {
      setBoundaryError(() => {
        throw error;
      });
    },
  });
  const { data: roleData } = useQuery(GET_ROLES);
  const [createUser, { loading: createUserLoading }] = useMutation(CREATE_USER, {
    onCompleted: (data) => {
      if (data.createUser.user) {
        setIsOpen(false);
        client.resetStore();
        setConfirmationModalOpen(true);
        setNewUser(data.createUser.user);
      } else {
        setError(
          "There was a problem creating the user, please check the data and try again"
        );
      }
    },
    onError: (err) => {
      const msg =
        err.graphQLErrors?.[0]?.message ||
        err.message ||
        "There was a problem creating the user, please check the data and try again";
      setError(msg);
    },
  });

  const switchModals = () => {
    setConfirmationModalOpen(false);
    setIsOpen(true);
  };

  const handleSubmit = (data: NewUserDataType) => {
    setError(undefined);
    createUser({
      variables: { input: { data: sanitizeCreateUserData(data) } },
    });
  };


  return (
    <>
      <Button
        primary
        icon={<ArrowRight />}
        onClick={() => {
          setIsOpen(true);
        }}
      >
        Create new user
      </Button>
      <Modal
        isOpen={isOpen}
        onClose={() => setIsOpen(false)}
        title="Create new user account"
      >
        {error && (
          <NotificationBanner error content={error} className={styles.error} />
        )}
        <UserForm
          onCancel={() => setIsOpen(false)}
          onSubmit={(data) => handleSubmit(data)}
          locations={locationData?.locations || []}
          roles={
            roleData?.roles.filter(
              (r) => r.type === "sales_rep" || r.type === "authorizer"
            ) || []
          }
          submitting={createUserLoading}
          defaultOption={dashboard}
        />
      </Modal>
      {newUser && (
        <Modal
          title={
            isSalesRep(newUser)
              ? "Motion Representative created"
              : isAuthorizer(newUser)
              ? "Authorizer created"
              : "User created"
          }
          isOpen={isConfirmationModalOpen}
          onClose={() => {
            setConfirmationModalOpen(false);
          }}
        >
          <NewUserConfirmation
            newUser={newUser}
            switchModals={() => switchModals()}
            setConfirmationModalOpen={() => setConfirmationModalOpen(false)}
          />
        </Modal>
      )}
    </>
  )
};

export default CreateNewUserButton;
